// Pure display helpers for the Segments list page (stats strip + filter bar
// sort + relative-time labels). Kept dependency-free (no React, no gateway)
// so they're trivially unit-testable and safe to import from the gateway
// normaliser without risking an import cycle.
import type {
  LoyaltySegment,
  SegmentsSummary,
} from '../../../types/loyaltySegments'

// The server seeds this preset with exactly this name; the "VIP Customers"
// stat keys off it rather than off member_count rank so renaming the preset
// drops the block instead of silently pointing at some other segment.
export const VIP_SEGMENT_NAME = 'VIP'

// ── Sort (filter bar dropdown) ───────────────────────────────────────────────
// Values mirror the mock's `#sortFilter` <option> values verbatim
// (utils/data/breadstack-audience-segments.html `applyFilters`).

export type SegmentsSort = 'size-desc' | 'size-asc' | 'ltv-desc' | 'name-asc'

export const DEFAULT_SEGMENTS_SORT: SegmentsSort = 'size-desc'

export const SEGMENTS_SORT_OPTIONS: { value: SegmentsSort; label: string }[] = [
  { value: 'size-desc', label: 'Largest First' },
  { value: 'size-asc', label: 'Smallest First' },
  { value: 'ltv-desc', label: 'Highest LTV' },
  { value: 'name-asc', label: 'A → Z' },
]

// Client-side sort over the already-loaded (and already search-filtered)
// segment list — same comparator as the mock's `applyFilters`. Returns a new
// array; never mutates `list`.
export const sortSegments = (
  list: LoyaltySegment[],
  sort: SegmentsSort,
): LoyaltySegment[] => {
  const sorted = [...list]
  switch (sort) {
    case 'size-desc':
      return sorted.sort(
        (a, b) => (b.member_count ?? 0) - (a.member_count ?? 0),
      )
    case 'size-asc':
      return sorted.sort(
        (a, b) => (a.member_count ?? 0) - (b.member_count ?? 0),
      )
    case 'ltv-desc':
      return sorted.sort((a, b) => (b.avg_ltv ?? 0) - (a.avg_ltv ?? 0))
    case 'name-asc':
      return sorted.sort((a, b) => a.name.localeCompare(b.name))
    default:
      return sorted
  }
}

// ── Stats strip summary fallback ─────────────────────────────────────────────
// Mirrors the mock's `renderStats` maths exactly (same file, `renderStats`):
// avg segment size and the LTV weighting both divide by the summed
// member_count, not by segment count or by the distinct `customers_covered`.
export const segmentsSummaryFallback = (
  list: LoyaltySegment[],
  customersCovered: number,
): SegmentsSummary => {
  const totalSegments = list.length
  const memberSum = list.reduce((acc, s) => acc + (s.member_count ?? 0), 0)
  const avgSegmentSize =
    totalSegments > 0 ? Math.round((memberSum / totalSegments) * 10) / 10 : 0
  const weightedAvgLtv =
    memberSum > 0
      ? Math.round(
          (list.reduce(
            (acc, s) => acc + (s.avg_ltv ?? 0) * (s.member_count ?? 0),
            0,
          ) /
            memberSum) *
            100,
        ) / 100
      : 0
  const vip = list.find((s) => s.name === VIP_SEGMENT_NAME)

  return {
    total_segments: totalSegments,
    customers_covered: customersCovered,
    avg_segment_size: avgSegmentSize,
    weighted_avg_ltv: weightedAvgLtv,
    vip_customers: vip?.member_count ?? 0,
  }
}

// ── Relative time ────────────────────────────────────────────────────────────

// 'Updated 2h ago' style relative label from an ISO timestamp (segment
// `last_evaluated_at`, member `last_order_at` / `last_seen_at`, …).
export const formatRelativeUpdated = (
  iso: string | null | undefined,
): string => {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const diffMs = Date.now() - date.getTime()
  if (diffMs < 60_000) return 'just now'
  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}
